import { useEffect, useState } from 'react';
import { Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Stack, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityOnIcon from '@mui/icons-material/Visibility';
import axios from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

const GET_REGISTRATION_CODE = "/registrationCode/"

const RegistrationCodes = () => {

  const auth = useAuth();
  const [allCodes, setAllCodes] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuIndex, setMenuIndex] = useState(null);
  const [showExpired, setShowExpired] = useState(false);

  const [filteredCodes, setFilteredCodes] = useState([]);

    useEffect(() => {
        axios.post(GET_REGISTRATION_CODE, {
            token: localStorage.getItem('token'),
            returnListOfRegistrationCodes: true
        }).then((response) => {
            if (response.data?.code === 0) {
                let tempAllCodes = response.data?.message?.listOfRegistrationCodes;
                tempAllCodes = tempAllCodes.map(code => ({
                    ...code,
                    expired: new Date(code.expiresAt) < new Date(),
                    code: code.code?.length === 8 ? `${code.code.slice(0, 4)}-${code.code.slice(4)}` : code.code
                }));

                setAllCodes(tempAllCodes);
                updateFilteredCodes(tempAllCodes, showExpired);
            } else if (response.data?.code === 327) {
                console.log(response.data?.message);
            } else {
                console.log(response);
            }
        })
        .catch((error) => {
            console.error('Error retrieving registration codes:', error);
        });
    }, []);

    const sortCodes = (codes) => {
        return codes.sort((a, b) => {
            const getStatusRank = (code) => {
            if (!code.used && !code.expired) return 0; // Available
                if (code.used) return 1; // Used
                return 2; // Expired
            };

            const statusComparison = getStatusRank(a) - getStatusRank(b);
            if (statusComparison !== 0) return statusComparison;

            return new Date(a.expiresAt) - new Date(b.expiresAt);
        });
    };

    // Then call this before setting filteredCodes
    const updateFilteredCodes = (allCodesList, showExpiredFlag) => {
        const visibleCodes = allCodesList.filter(code => showExpiredFlag || (!code.expired && !code.used));
        const sortedCodes = sortCodes(visibleCodes);
        setFilteredCodes(sortedCodes);
    };

  const handleToggleShowExpired = () => {
      const newShowExpired = !showExpired;
      setShowExpired(newShowExpired);
      updateFilteredCodes(allCodes, newShowExpired);
  };

  const handleMenuClick = (event, index) => {
    setAnchorEl(event.currentTarget);
    setMenuIndex(index);
  };

  function updateExpired(regCodeId) {
    expireRegistrationCode(regCodeId);
    setAnchorEl(null);
    setMenuIndex(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuIndex(null);
  };

  function expireRegistrationCode(regCodeId) {
    axios.post(GET_REGISTRATION_CODE, {
        token: localStorage.getItem('token'),
        updateExpired: true,
        regCodeId: regCodeId
    }).then((response) => {
        if (response.data?.code === 0) {
            console.log(response.data?.message?.updated)
            const date = new Date().toLocaleDateString('en-US', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });

            let tempAllCodes = allCodes.map(code =>
            code.id === regCodeId
                ? { ...code, expired: true, expiresAt: date }
                : code
            );
            setAllCodes(tempAllCodes);
            updateFilteredCodes(tempAllCodes, showExpired)
            
        } else if (response.data?.code === 411) {
            console.log(response.data?.message);
        }else {
            console.log(response)
        }
    })
    .catch((error) => {
        console.error('Updating registration code expired status:', error);
    });
  }

  function createCode() {
    setLoading(true)
    axios.post(GET_REGISTRATION_CODE, {
        token: localStorage.getItem('token'),
        addRegistrationCode: true
    }).then((response) => {
        if (response.data?.code === 0) {
            const newCode = {
                ...response.data?.message?.registrationCode, 
                createdBy: auth.userName,
                code: response.data?.message?.registrationCode.code?.length === 8 ? `${response.data?.message?.registrationCode.code.slice(0, 4)}-${response.data?.message?.registrationCode.code.slice(4)}` : response.data?.message?.registrationCode.code
            };

            const tempAllCodes = [...allCodes, newCode];
            setAllCodes(tempAllCodes);
            updateFilteredCodes(tempAllCodes, showExpired)
        } else if (response.data?.code === 327) {
            console.log(response.data?.message);
        } else {
            console.log(response);
        }
    })
    .catch((error) => {
        console.error('Error retrieving registration code:', error);
    }).finally(() => {
        setLoading(false)
    })
  }

const formatDateExpires = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const [loading, setLoading] = useState(false);

  return (
    <div style={{marginBottom: '30px'}}>
      <Typography variant="h6" gutterBottom>
        Registration Codes
      </Typography>

      <Typography variant="body1" color="text.secondary" gutterBottom>
        To add a device: (1) Create a code. (2) Download and launch the OcuBuild app on an approved device. (3) Enter the code when asked.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" mt={2} mb={2}>
        <Button
            variant="text"
            sx={{ fontWeight: 'bold', color: '#1a73e8' }}
            onClick={createCode}
            disabled={loading}
            loading={loading}
            loadingPosition="start"
            >
            + CREATE CODE
        </Button>
        <Button
          variant="text"
          startIcon={showExpired ? <VisibilityOnIcon/> : <VisibilityOffIcon/>}
          sx={{ color: '#1a73e8' }}
          onClick={handleToggleShowExpired}
        >
          {showExpired ? 'HIDE USED/EXPIRED' : 'SHOW USED/EXPIRED'}
        </Button>
      </Stack>

      <TableContainer elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Code</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Expiration</strong></TableCell>
              <TableCell><strong>Created by</strong></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCodes.map((row, index) => (
              <TableRow key={index}>
                <TableCell sx={(row.expired || row.used) ? { color: 'text.disabled' } : {}}>{row.code}</TableCell>
                <TableCell sx={(row.expired || row.used) ? { color: 'text.disabled' } : {}}>{row.used === true
                                                                                    ? 'Used'
                                                                                    : new Date(row.expiresAt) < new Date()
                                                                                    ? 'Expired'
                                                                                    : 'Available'}</TableCell>
                <TableCell sx={(row.expired || row.used) ? { color: 'text.disabled' } : {}}>{formatDateExpires(row.expiresAt)}</TableCell>
                <TableCell sx={(row.expired || row.used) ? { color: 'text.disabled' } : {}}>{row.createdBy}</TableCell>
                <TableCell align="right">
                  {!(row.expired || row.used) && (
                    <>
                      <IconButton size="small" onClick={(e) => handleMenuClick(e, index)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && menuIndex === index}
                        onClose={handleClose}
                      >
                        <MenuItem onClick={() => updateExpired(row.id)}>Expire Now</MenuItem>
                      </Menu>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default RegistrationCodes;